import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  YouTubeLiveStatus,
  YouTubeResolveResult,
  YouTubeSyncResult,
} from '../types';
import { ApiError, api } from './client';
import { qk } from './keys';

/**
 * Poll a programme's live status. Refetch every 30s while the user is on the
 * page; the API caches under the hood (~30s while live, ~1–5min when not),
 * so polling is cheap from a quota perspective and keeps the badge fresh.
 *
 * Returns `null` when the programme has no YouTube source configured (the API
 * still returns 200 with `configured: false` in that case).
 */
export function useYouTubeLive(programmeId: string | null) {
  return useQuery({
    queryKey: qk.youtubeLive(programmeId ?? ''),
    enabled: !!programmeId,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    queryFn: () =>
      api
        .get<YouTubeLiveStatus>(
          `/admin/programmes/${encodeURIComponent(programmeId ?? '')}/youtube/live`,
        )
        // 503 = YOUTUBE_API_KEY not configured. Surface as null so the UI hides
        // the badge instead of looking broken.
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 503) return null;
          throw err;
        }),
  });
}

export function useResolveYouTubeChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (programmeId: string) =>
      api.post<YouTubeResolveResult>(
        `/admin/programmes/${encodeURIComponent(programmeId)}/youtube/resolve`,
        {},
      ),
    // Resolving writes the discovered `youtubeChannelId` back onto the
    // programme — invalidate **both** keys (`['programmes']` list and
    // `['programme', …]` detail) so the detail panel's form re-pulls the
    // updated value rather than showing the pre-resolve handle.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['programmes'] });
      void qc.invalidateQueries({ queryKey: ['programme'] });
    },
  });
}

export function useSyncYouTube() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { programmeId: string; max?: number; full?: boolean }) =>
      api.post<YouTubeSyncResult>(
        `/admin/programmes/${encodeURIComponent(args.programmeId)}/youtube/sync`,
        { max: args.max, full: args.full },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['episodes', vars.programmeId] });
      qc.invalidateQueries({ queryKey: qk.youtubeLive(vars.programmeId) });
    },
  });
}

export type DetectLiveResult =
  | {
      detected: true;
      videoId: string;
      title: string | null;
      thumbnailUrl: string | null;
      actualStartTime: string | null;
      concurrentViewers: number | null;
    }
  | { detected: false; reason: 'not-live' };

/**
 * Manual-click YouTube live-stream discovery for a programme — one
 * `search.list` (100 quota units) that finds the currently-airing
 * broadcast on the programme's configured YouTube channel id / handle
 * and auto-pins its video id. Precondition: the programme row must
 * have `youtubeChannelId` OR `youtubeHandle` set.
 */
export function useDetectProgrammeLive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (programmeId: string) =>
      api.post<DetectLiveResult>(
        `/admin/programmes/${encodeURIComponent(programmeId)}/youtube/detect-live`,
        {},
      ),
    // Detection writes `youtubeLiveStreamId` — invalidate BOTH the
    // programmes list and the detail cache so the form re-pulls the
    // updated value, and the live-badge query so the "Live now" state
    // reflects reality on the next poll.
    onSuccess: (_data, programmeId) => {
      void qc.invalidateQueries({ queryKey: ['programmes'] });
      void qc.invalidateQueries({ queryKey: ['programme'] });
      void qc.invalidateQueries({ queryKey: qk.youtubeLive(programmeId) });
    },
  });
}
